import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchWithHeaders, fileUtils } from '@sendible/common';
import { useToastNotificationContext } from '@sendible/design-system';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { useTranslation } from 'react-i18next';
import { MediaIdType, MediaType, useBridgeContext } from '@sendible/shared-state-bridge';
import endpoints from '../../data-layer/endpoints';
import { useMediaLibraryContext } from '../../pages/MediaLibrary/context';
import { useInfiniteQueryWithAccessToken, useMutationWithAccessToken, useQueryWithAccessToken } from '../../data-layer/useReactQueryWithAccessToken';
import { mediaRenameFromPreview } from '../../pages/MediaLibrary/pendoEvents';
import { getUrlWithCredentialsFromUser } from '../../utils';

export const useGetMediasQueryKey = (): DLInfiniteQueryKey => {
  const [
    {
      user: { userId },
    },
  ] = useBridgeContext();

  const { activeMediaLibrary, mediaSearch, perPage } = useMediaLibraryContext();
  const paramObj = {
    mediaLibraryId: activeMediaLibrary?.id,
    name: mediaSearch,
    status: 'Ready,Uploaded,Processing,New',
    perPage,
    dashboardId: userId,
  };

  return [endpoints.GetMedias.endpoint, paramObj];
};
export const useGetMediasById = (mediaIds: MediaIdType[], sessionId?: string) => {
  return useQueryWithAccessToken<MediaType[]>([endpoints.GetMedia.endpoint, { ids: mediaIds, sessionId }], {
    enabled: !!mediaIds.length,
    staleTime: Infinity,
    select: (medias) => {
      const typedMedias = medias as MediaType[];

      return typedMedias.map((media) => ({ ...media, name: fileUtils.sanitizeFileName(media.name) }));
    },
  });
};
export const usePollMediasById = (uploadingMediaIds: MediaIdType[]) => {
  return useQueryWithAccessToken<MediaType[]>([endpoints.GetMedia.endpoint, { ids: uploadingMediaIds }], {
    refetchInterval: endpoints.GetMedia.interval,
    enabled: !!uploadingMediaIds.length,
    select: (medias) => {
      const typedMedias = medias as MediaType[];

      return typedMedias.map((media) => ({ ...media, name: fileUtils.sanitizeFileName(media.name) }));
    },
  });
};

export const useInfiniteGetMedias = () => {
  const { activeMediaLibrary } = useMediaLibraryContext();
  const mediasQueryKey = useGetMediasQueryKey();

  return useInfiniteQueryWithAccessToken<MediaType[]>(mediasQueryKey, {
    enabled: !!activeMediaLibrary?.id,
    refetchInterval: endpoints.GetMedias.interval,
  } as unknown as UseInfiniteQueryOptionsWithGenerics<MediaType[]>);
};

export const useDeleteMedia = (pendoEvent: string, mediaType?: string) => {
  const queryClient = useQueryClient();
  const mediasQueryKey = useGetMediasQueryKey();
  const { selectedMediasIds, setSelectedMediasIds } = useMediaLibraryContext();
  const { t } = useTranslation('media_library');
  const { method, endpoint } = endpoints.DeleteMedia;
  const { showToastNotification } = useToastNotificationContext();

  return useMutationWithAccessToken<MediaType, MediaMutationParamsType>({
    method,
    endpoint,
    options: {
      onSuccess: useCallback(
        (result: unknown) => {
          const { mediaId, name } = result as { mediaId: MediaIdType; name: string };

          showToastNotification({
            notificationText: t('toast_single_media_deletion_success', { mediaName: name }) || '',
            type: NotificationTypeEnum.Success,
            showCloseButton: true,
          });

          window.pendo.track(pendoEvent, {
            name,
            type: mediaType,
          });
          queryClient.invalidateQueries({ queryKey: mediasQueryKey });

          // If the media is selected, deselect it
          if (selectedMediasIds.includes(mediaId)) {
            const newList = [...selectedMediasIds];

            newList.splice(newList.indexOf(mediaId), 1);
            setSelectedMediasIds(newList);
          }
        },
        [selectedMediasIds]
      ),
      onError: useCallback((_: unknown, variables: unknown) => {
        const { name } = variables as { name: string };

        showToastNotification({
          notificationText: t('toast_medias_deletion_error', { name }) || '',
          type: NotificationTypeEnum.Error,
          showCloseButton: true,
        });
      }, []),
    },
  });
};

export const useDeleteMultipleMedias = (pendoEvent: string) => {
  const queryClient = useQueryClient();
  const mediasQueryKey = useGetMediasQueryKey();
  const { t } = useTranslation('media_library');
  const { showToastNotification } = useToastNotificationContext();

  const { method, endpoint } = endpoints.DeleteMedia;

  return useMutationWithAccessToken<MediaType[], { selectedMediasIds: string[]; access_token: string }>({
    method,
    endpoint,
    options: {
      onSuccess: (selectedMediasIds) => {
        queryClient.invalidateQueries({ queryKey: mediasQueryKey });

        showToastNotification({
          notificationText: t('toast_multiple_medias_deletion_success', { numberOfMedias: selectedMediasIds.length }) || '',
          type: NotificationTypeEnum.Success,
          showCloseButton: true,
        });

        window.pendo.track(pendoEvent, { selectedMediasIds });
      },
      onError: () => {
        showToastNotification({
          notificationText: t('toast_multiple_medias_deletion_error') || '',
          type: NotificationTypeEnum.Error,
          showCloseButton: true,
        });
      },
    },
    mutationFn: async ({ selectedMediasIds, access_token }) => {
      const responses = await Promise.allSettled(
        selectedMediasIds.map(async (mediaId) => {
          const response = (await fetchWithHeaders({
            method,
            url: endpoint,
            params: { mediaId, access_token },
          })) as FetchWithHeadersResult<MediaType>;

          return response.result;
        })
      );

      const rejectedResponses = responses.filter((response) => response.status === 'rejected');

      if (rejectedResponses.length > 0) {
        throw new Error();
      }

      return responses.filter((response) => response.status === 'fulfilled').map((response) => (response as PromiseFulfilledResult<MediaType>).value);
    },
  });
};

export const useRenameMedia = (mediaType: string, newName: string) => {
  const { t } = useTranslation('media_library');
  const queryClient = useQueryClient();
  const mediasQueryKey = useGetMediasQueryKey();
  const { showToastNotification } = useToastNotificationContext();

  return useMutationWithAccessToken<MediaType, MediaMutationParamsType>({
    method: endpoints.RenameMedia.method,
    endpoint: endpoints.RenameMedia.endpoint,
    body: { name: newName },
    options: {
      onSuccess: useCallback((result: unknown) => {
        const { name } = result as { name: string };

        showToastNotification({
          notificationText: t('toast_media_rename_success') || '',
          type: NotificationTypeEnum.Success,
          showCloseButton: true,
        });

        window.pendo.track(mediaRenameFromPreview, {
          name,
          type: mediaType,
        });
        queryClient.invalidateQueries({ queryKey: mediasQueryKey });
      }, []),
      onError: useCallback(() => {
        showToastNotification({
          notificationText: t('toast_media_rename_error') || '',
          type: NotificationTypeEnum.Error,
          showCloseButton: true,
        });
      }, []),
    },
  });
};

export const useQueryMediaEndpontForSourceSets = (id: number, user: ApiUser, shouldUse = true) => {
  const queryResult = useQuery({
    queryKey: [endpoints.GetMedia.endpoint, { ids: id }],
    queryFn: async () => {
      const { result } = (await fetchWithHeaders({
        method: 'GET',
        url: `${getUrlWithCredentialsFromUser(`${endpoints.GetMedia.endpoint}`, user)}&ids=${id}`,
      })) as FetchWithHeadersResult<MediaType[]>;

      return result;
    },
    select: (medias: MediaType[]) => medias,
    refetchInterval: (query) => {
      const media = (query.state.data as MediaType[] | undefined)?.[0];

      return media?.sourceSets?.length ? false : endpoints.GetMedia.interval;
    },
    refetchOnWindowFocus: false,
    enabled: shouldUse,
  });

  return { ...queryResult };
};
